/**
 * Focus (720): the flow Focus expects of a player, over the same parts as the default
 * (docs/proposals/player-providers.md, A.2–A.3).
 *
 * Learning: questions are checked one by one (`onCheck`), each answer is reported when it is
 * checked, and "I'm done" opens once every page is completed (D107). Evaluation (`mode=evaluation`):
 * the learner sees no Check, hint or mark, ever (exam + `none`); "I'm done" warns about unanswered
 * questions, then submits — the server scores, and the result goes only to Focus.
 *
 * Launched by Focus (`slxapi`, D75), the statements are Focus's learner's, under Focus's
 * registration and component, sent to Focus's LRS, and the attempt is kept in Focus's state API —
 * `launch.ts` reads the launch, `state.ts` keeps the attempt.
 *
 * One bar (D98): the lesson's tools (AI, My words) on the left, the page's Check in the middle, the
 * pager on the right. A disabled Next is all that says the page is not done. A page holds one question.
 *
 * A page is reported `completed` in Focus's format (`completePages`) when the learner presses Next in
 * the navigation bar on it, with the mean score of its marked questions. Its questions are the ones
 * answered on it, until the page→questions map exists (proposal Part 2, Phase 4) — which is also
 * what page-by-page unlocking and "showing the answer locks the question" still wait for.
 */

import { captureFocusLaunch } from './launch.js';
import type { PlayerProvider } from '../types.js';

export const FOCUS_PROVIDER: PlayerProvider = {
  id: 'focus',
  footer: {
    start: ['tools'],
    middle: ['check', 'status'],
    // No score badge: Focus shows the learner no score or count of right answers in the bar.
    end: ['pager', 'finish'],
    // No labels: the footer speaks English, the defaults, as the lesson it plays does.
    nextWords: true,
  },
  xapi: { profile: 'focus', answeredAt: 'check', hints: true, completeOnDone: false, completePages: true },
  hostNavigation: true,
  // The footer's Check is the page's; the asset draws none (D98).
  hostCheck: true,
  // Page by page: Next waits until the asset says the page is done (`canAdvance`).
  navigation: 'linear',
  // Two checks of a question, then its answer may be shown — the 720 rule.
  solutionAfter: 2,
  // Check waits for an answer (no `checkUnanswered`) — the 720 rule.
  // The free-talk bot, over the activity area; `inline` puts it in the step's own box (D78).
  external: { services: ['free-talk'], placement: 'panel' },
  finish: { learning: 'allCompleted' },
  evaluation: true,
  defaultFeedback: 'onCheck',
  // Focus's learner, attempt, component and LRS, from `slxapi` (D75).
  launch: captureFocusLaunch,
  // English, like the footer.
  texts: {
    unanswered: (count) => ({
      message: count === 1 ? 'One question is unanswered. Finish anyway?' : `${count} questions are unanswered. Finish anyway?`,
      ok: 'Finish',
      cancel: 'Back to the questions',
    }),
  },
};
